import { useState, useEffect, useRef } from "react";

// --- API URL ---
const SCRIPT_URL = "https://script.google.com/macros/s/AKfycbxZL9N5rxe6nV1ZGoFHKTf36URSy2PzuBctC4T40A4cbDx8rSfUqXSTgN0Z8KhclQnFcw/exec"; 

// 🛠️ SMART PICK FUNCTION
const pick = (obj, ...keys) => {
  if (!obj) return null;
  const safeObj = {};
  for (let k in obj) safeObj[k.toLowerCase().trim()] = obj[k];
  for (let key of keys) {
    const safeKey = key.toLowerCase().trim();
    if (safeObj[safeKey] !== undefined && safeObj[safeKey] !== null && String(safeObj[safeKey]).trim() !== "") {
      return safeObj[safeKey];
    }
  }
  return null;
};

export default function AdminEmployeePage() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  
  const [photo, setPhoto] = useState(null);            // displayed src (saved URL or local preview)
  const [photoSaved, setPhotoSaved] = useState(false); // true once persisted to the server
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoError, setPhotoError] = useState(null);
  const fileInputRef = useRef(null);

  // Main API base already includes /api; strip it when building image URLs.
  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const IMG_BASE = API_BASE ? API_BASE.replace("/api", "") : "";

  const getSelectedCnic = () => {
    const c = pick(selectedEmployee, "CNIC | شناختی کارڈ");
    const s = c ? String(c).trim() : "";
    return s && s !== "-" ? s : "";
  };

  useEffect(() => {
    fetch(SCRIPT_URL)
      .then(async (res) => {
        const text = await res.text();
        try {
          return JSON.parse(text);
        } catch (e) {
          throw new Error("API Issue: " + text.substring(0, 100));
        }
      })
      .then((json) => {
        if (json.success && json.data) {
          setEmployees(json.data);
        } else {
          setErrorMsg("Data fetch failed: " + JSON.stringify(json));
        }
      })
      .catch((err) => setErrorMsg(err.message))
      .finally(() => setLoading(false));
  }, []);

  // Load any saved profile photo for the opened employee (keyed by CNIC).
  useEffect(() => {
    setPhoto(null);
    setPhotoSaved(false);
    setPhotoError(null);
    if (!selectedEmployee) return;
    const cnic = getSelectedCnic();
    if (!cnic) return;
    let cancelled = false;
    fetch(`${API_BASE}/employee-photos/${encodeURIComponent(cnic)}`)
      .then((r) => r.json())
      .then((json) => {
        if (cancelled) return;
        if (json.success && json.photo_url) {
          setPhoto(`${IMG_BASE}${json.photo_url}`);
          setPhotoSaved(true);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmployee]);

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const cnic = getSelectedCnic();
    if (!cnic) { setPhotoError("This employee has no CNIC on record, so a photo can't be saved."); return; }
    if (!file.type.startsWith("image/")) { setPhotoError("Please choose an image file."); return; }

    // instant local preview while uploading
    setPhoto(URL.createObjectURL(file));
    setPhotoSaved(false);
    setUploadingPhoto(true);
    setPhotoError(null);

    try {
      const fd = new FormData();
      fd.append("photo", file);
      fd.append("cnic", cnic);
      const res = await fetch(`${API_BASE}/employee-photos`, { method: "POST", body: fd });
      const json = await res.json();
      if (json.success && json.photo_url) {
        setPhoto(`${IMG_BASE}${json.photo_url}`);
        setPhotoSaved(true);
      } else {
        setPhotoError(json.message || "Upload failed.");
      }
    } catch (err) {
      setPhotoError("Upload failed. Check your connection.");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleBackToList = () => {
    setSelectedEmployee(null);
    setPhoto(null);
    setPhotoSaved(false);
    setUploadingPhoto(false);
    setPhotoError(null);
  };

  return (
    <div style={{ height: "100%", maxHeight: "100vh", overflowY: "auto", backgroundColor: "#f8fafc" }}>
      
      {!selectedEmployee ? (
        <div style={{ maxWidth: "1000px", margin: "0 auto", padding: "30px 20px", fontFamily: "sans-serif", paddingBottom: "80px" }}>
          <h2 style={{ fontSize: "24px", color: "#1e293b", marginBottom: "20px" }}>Employee Directory</h2>
          
          {errorMsg && (
            <div style={{ backgroundColor: "#fee2e2", color: "#b91c1c", padding: "16px", borderRadius: "8px", marginBottom: "20px", border: "1px solid #f87171" }}>
              <strong>Error: </strong> {errorMsg}
            </div>
          )}

          <div style={{ backgroundColor: "#fff", borderRadius: "10px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)", overflowX: "auto", border: "1px solid #e2e8f0" }}>
            
            {loading ? ( 
              <div style={{ padding: "40px", textAlign: "center", color: "#3b82f6", fontWeight: "600" }}>⏳ Loading Data...</div>
            ) : employees.length === 0 && !errorMsg ? (
              <div style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>🚫 Koi data nahi mila.</div>
            ) : employees.length > 0 ? (
              <table style={{ width: "100%", minWidth: "700px", borderCollapse: "collapse", textAlign: "left" }}>
                <thead>
                  <tr style={{ backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                    <th style={{ padding: "16px 20px", color: "#64748b", fontSize: "13px", fontWeight: "600", textTransform: "uppercase" }}>Name</th>
                    <th style={{ padding: "16px 20px", color: "#64748b", fontSize: "13px", fontWeight: "600", textTransform: "uppercase" }}>Role</th>
                    <th style={{ padding: "16px 20px", color: "#64748b", fontSize: "13px", fontWeight: "600", textTransform: "uppercase" }}>Site</th>
                    <th style={{ padding: "16px 20px", color: "#64748b", fontSize: "13px", fontWeight: "600", textTransform: "uppercase" }}>Status</th>
                    <th style={{ padding: "16px 20px", textAlign: "right", color: "#64748b", fontSize: "13px", fontWeight: "600", textTransform: "uppercase" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.map((emp, idx) => {
                    // AAPKI SHEET KE EXACT HEADERS YAHAN MAP KIYE HAIN
                    const name = pick(emp, "Candidate Name | امیدوار کا نام") || "Unknown";
                    const role = pick(emp, "Position | عہدہ") || "-";
                    const site = pick(emp, "Area/Site | علاقہ / سائٹ") || "-";
                    const status = pick(emp, "Hiring Decision | بھرتی کا فیصلہ") || "Pending";
                    const isActive = status.toLowerCase() === "selected" || status.toLowerCase() === "hired";

                    return (
                      <tr key={idx} style={{ borderBottom: "1px solid #f1f5f9", transition: "background 0.2s" }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                        <td style={{ padding: "16px 20px", fontWeight: "500", color: "#0f172a" }}>{name}</td>
                        <td style={{ padding: "16px 20px", color: "#475569" }}>{role}</td>
                        <td style={{ padding: "16px 20px", color: "#475569" }}>{site}</td>
                        <td style={{ padding: "16px 20px" }}>
                          <span style={{ 
                            padding: "4px 10px", borderRadius: "20px", fontSize: "12px", fontWeight: "600",
                            backgroundColor: isActive ? "#dcfce7" : (status.includes("Pending") ? "#fef3c7" : "#fee2e2"),
                            color: isActive ? "#166534" : (status.includes("Pending") ? "#92400e" : "#991b1b")
                          }}>
                            {status}
                          </span>
                        </td>
                        <td style={{ padding: "16px 20px", textAlign: "right" }}>
                          <button 
                            onClick={() => setSelectedEmployee(emp)}
                            style={{ padding: "6px 14px", backgroundColor: "#3b82f6", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "13px", fontWeight: "500", transition: "0.2s" }}
                          >
                            View Profile
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : null}
          </div>
        </div>
      ) : (
        <div style={{ maxWidth: "800px", margin: "0 auto", padding: "30px 20px", fontFamily: "sans-serif", color: "#333", paddingBottom: "80px" }}>
          
          <button 
            onClick={handleBackToList}
            style={{ background: "none", border: "none", color: "#64748b", cursor: "pointer", fontSize: "14px", fontWeight: "600", display: "flex", alignItems: "center", gap: "6px", marginBottom: "20px", padding: 0 }}
          >
            &larr; Back to Employees
          </button>

          {(() => {
            // AAPKI SHEET KE EXACT HEADERS YAHAN MAP KIYE HAIN
            const p_name = pick(selectedEmployee, "Candidate Name | امیدوار کا نام") || "Unknown";
            const p_role = pick(selectedEmployee, "Position | عہدہ") || "-";
            const p_site = pick(selectedEmployee, "Area/Site | علاقہ / سائٹ") || "-";
            const p_status = pick(selectedEmployee, "Hiring Decision | بھرتی کا فیصلہ") || "Pending";
            const p_phone = pick(selectedEmployee, "Contact No. | رابطہ نمبر") || "-";
            const p_cnic = pick(selectedEmployee, "CNIC | شناختی کارڈ") || "-";
            const p_education = pick(selectedEmployee, "Q3. Qualification | تعلیمی قابلیت") || "-";
            const p_experience = pick(selectedEmployee, "Q2. Work Experience | کام کا تجربہ") || "-";
            
            // Evaluation details
            const p_totalScore = pick(selectedEmployee, "Total Score | کل نمبر") || "0";
            const p_outOf = pick(selectedEmployee, "Out Of | کل ممکنہ نمبر") || "100";
            const p_remarks = pick(selectedEmployee, "Remarks | ریمارکس") || "No remarks provided.";
            const p_evaluator = pick(selectedEmployee, "Evaluator Name | جائزہ لینے والے کا نام") || "-";
            const p_date = pick(selectedEmployee, "Date | تاریخ") || pick(selectedEmployee, "Submitted At | جمع کرانے کا وقت") || "-";
            
            const isProfileActive = p_status.toLowerCase() === "selected" || p_status.toLowerCase() === "hired";

            return (
              <>
                {photoError && (
                  <div style={{ backgroundColor: "#fee2e2", color: "#b91c1c", padding: "10px 14px", borderRadius: "8px", marginBottom: "12px", border: "1px solid #f87171", fontSize: "13px", fontWeight: 600 }}>
                    {photoError}
                  </div>
                )}
                {!photoSaved && getSelectedCnic() && (
                  <div style={{ backgroundColor: "#fef2f2", color: "#b91c1c", padding: "10px 14px", borderRadius: "8px", marginBottom: "12px", border: "1px solid #fecaca", fontSize: "13px", fontWeight: 600, display: "flex", alignItems: "center", gap: "8px" }}>
                    ⚠ Profile picture is required — click the avatar to upload.
                  </div>
                )}
                {!getSelectedCnic() && (
                  <div style={{ backgroundColor: "#fffbeb", color: "#92400e", padding: "10px 14px", borderRadius: "8px", marginBottom: "12px", border: "1px solid #fde68a", fontSize: "13px", fontWeight: 600 }}>
                    No CNIC on record for this employee — a profile picture can't be saved until a CNIC exists.
                  </div>
                )}
                <div style={{ display: "flex", alignItems: "center", gap: "24px", padding: "24px", backgroundColor: "#fff", borderRadius: "12px", border: "1px solid #e2e8f0", marginBottom: "20px" }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "6px", flexShrink: 0 }}>
                    <div
                      onClick={() => { if (getSelectedCnic()) fileInputRef.current.click(); }}
                      style={{ width: "90px", height: "90px", borderRadius: "50%", backgroundColor: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", cursor: getSelectedCnic() ? "pointer" : "not-allowed", overflow: "hidden", position: "relative", border: `2px solid ${photoSaved ? "#cbd5e1" : "#ef4444"}`, boxShadow: photoSaved ? "none" : "0 0 0 3px rgba(239,68,68,0.15)" }}
                    >
                      {photo ? (
                        <img src={photo} alt="Profile" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      ) : (
                        <span style={{ fontSize: "28px", color: "#94a3b8", fontWeight: "600" }}>{p_name.charAt(0).toUpperCase()}</span>
                      )}
                      <div style={{ position: "absolute", bottom: 0, width: "100%", backgroundColor: "rgba(0,0,0,0.6)", color: "#fff", fontSize: "10px", textAlign: "center", padding: "4px 0" }}>
                        {uploadingPhoto ? "Uploading…" : photoSaved ? "Edit" : "Add Photo *"}
                      </div>
                    </div>
                    {uploadingPhoto ? (
                      <span style={{ fontSize: "11px", fontWeight: 600, color: "#3b82f6" }}>Uploading…</span>
                    ) : photoSaved ? (
                      <span style={{ fontSize: "11px", fontWeight: 600, color: "#16a34a" }}>✓ Saved</span>
                    ) : (
                      <span style={{ fontSize: "11px", fontWeight: 600, color: "#ef4444" }}>● Required</span>
                    )}
                  </div>
                  <input type="file" accept="image/*" ref={fileInputRef} onChange={handleImageChange} style={{ display: "none" }} />

                  <div style={{ flex: 1 }}>
                    <h1 style={{ margin: "0 0 6px 0", fontSize: "24px", color: "#0f172a" }}>{p_name}</h1>
                    <p style={{ margin: "0 0 10px 0", color: "#64748b", fontSize: "15px" }}>{p_role} &nbsp;•&nbsp; {p_site}</p>
                    <span style={{ padding: "4px 12px", backgroundColor: isProfileActive ? "#dcfce7" : (p_status.includes("Pending") ? "#fef3c7" : "#fee2e2"), color: isProfileActive ? "#166534" : (p_status.includes("Pending") ? "#92400e" : "#991b1b"), borderRadius: "20px", fontSize: "12px", fontWeight: "600" }}>
                      {p_status}
                    </span>
                  </div>
                  
                  {/* Score Card on Top Right */}
                  <div style={{ textAlign: "center", padding: "10px 20px", backgroundColor: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                    <div style={{ fontSize: "11px", color: "#64748b", fontWeight: "600", textTransform: "uppercase" }}>Total Score</div>
                    <div style={{ fontSize: "24px", fontWeight: "bold", color: "#0f172a", marginTop: "4px" }}>
                      {p_totalScore} <span style={{ fontSize: "14px", color: "#94a3b8" }}>/ {p_outOf}</span>
                    </div>
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "20px", marginBottom: "20px" }}>
                  <div style={{ padding: "24px", backgroundColor: "#fff", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
                    <h3 style={{ borderBottom: "1px solid #f1f5f9", paddingBottom: "12px", marginTop: 0, color: "#1e293b", fontSize: "16px" }}>Personal Details</h3>
                    <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginTop: "16px", fontSize: "14px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ color: "#64748b" }}>Contact No.</span> <span style={{ fontWeight: "500" }}>{p_phone}</span></div>
                      <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ color: "#64748b" }}>CNIC</span> <span style={{ fontWeight: "500", fontFamily: "monospace", fontSize: "13px" }}>{p_cnic}</span></div>
                      <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ color: "#64748b" }}>Date applied</span> <span style={{ fontWeight: "500" }}>{p_date}</span></div>
                    </div>
                  </div>

                  <div style={{ padding: "24px", backgroundColor: "#fff", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
                    <h3 style={{ borderBottom: "1px solid #f1f5f9", paddingBottom: "12px", marginTop: 0, color: "#1e293b", fontSize: "16px" }}>Professional Info</h3>
                    <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginTop: "16px", fontSize: "14px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ color: "#64748b" }}>Education</span> <span style={{ fontWeight: "500", textAlign: "right", maxWidth: "60%" }}>{p_education}</span></div>
                      <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ color: "#64748b" }}>Experience</span> <span style={{ fontWeight: "500", textAlign: "right", maxWidth: "60%" }}>{p_experience}</span></div>
                    </div>
                  </div>
                </div>

                {/* Interview / Evaluation Section */}
                <div style={{ padding: "24px", backgroundColor: "#fff", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
                  <h3 style={{ borderBottom: "1px solid #f1f5f9", paddingBottom: "12px", marginTop: 0, color: "#1e293b", fontSize: "16px" }}>Evaluation Comments & Remarks</h3>
                  <div style={{ marginTop: "16px" }}>
                    <div style={{ padding: "16px", backgroundColor: "#f8fafc", borderRadius: "8px", borderLeft: "4px solid #3b82f6" }}>
                      <p style={{ margin: "0 0 12px 0", color: "#334155", fontSize: "15px", fontStyle: "italic", lineHeight: "1.5" }}>"{p_remarks}"</p>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid #e2e8f0", paddingTop: "12px", fontSize: "13px" }}>
                        <span style={{ color: "#64748b" }}>Evaluated By: <strong>{p_evaluator}</strong></span>
                        <span style={{ color: "#64748b" }}>Date: {p_date}</span>
                      </div>
                    </div>
                  </div>
                </div>

              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}